import React, { useState, useMemo } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import { SimDetail } from 'utils/api/models/accounts/customerAccountResponse';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SimType } from 'utils/api/payloads/microApi';
import SimData from './subcomponents/SimData';
import Add from '@material-ui/icons/Add';
import { SimService } from 'services/sim/sim';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from 'components/CustomButtons/IconButton';
import Button from 'components/CustomButtons/Button';
import RemoveConfirmationDialog from './subcomponents/RemoveConfirmationDialog';
import ReplaceSimDialog from './subcomponents/ReplaceSimDialog';
import { getSimMgmtLabels } from 'redux/modules/dynamicLabels/selectors';
import classNames from 'classnames';
import ChargeBanner from '../ChargeBanner';
import { AuthConsumer } from 'components/Auth/authContext';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);

const ADD_SIM_CARD_ERROR = 'SIM_ERROR';

interface ISimDialogProps {
	isOpen: boolean;
	simType: SimType;
	hideDialog: () => void;
	phoneNumber: string;
	accountDetails: string;
	simsDetails?: (SimDetail | undefined)[];
	headerName: string;
	maxSimCards: number;
	reload: () => void;
}

export enum CardType {
	SIM,
	ESIM,
}

const SimDialog = ({
	isOpen,
	hideDialog,
	phoneNumber,
	accountDetails,
	simsDetails,
	headerName,
	maxSimCards,
	simType,
	reload,
}: ISimDialogProps) => {
	const classes = useStyles();
	const [loading, setLoading] = useState<boolean>(false);
	const [simNumber, setSimNumber] = useState<string>('');
	const [oldSimNumber, setOldSimNumber] = useState<string>('');
	const [simNumberValid, setSimNumberValid] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState<'removeCard' | 'replaceCard' | 'addCard' | undefined>();
	const [cardType, setCardType] = useState<CardType>(CardType.SIM);
	const dispatch = useDispatch();
	const simMgmtLabels = useSelector(getSimMgmtLabels);
	const [otp, setOtp] = useState<string>('');
	const [isIncorrectOtp, setIsInCorrectOtp] = useState(false);
	const [isSIMError, setIsSIMError] = useState(false);

	const showReplaceSimCardDialog = (oldSimNumber: string) => {
		setCardType(CardType.SIM);
		showReplaceCardDialog(oldSimNumber);
	};

	const showReplaceEsimCardDialog = (oldSimNumber: string) => {
		setCardType(CardType.ESIM);
		showReplaceCardDialog(oldSimNumber);
	};

	const showReplaceCardDialog = (oldSimNumber: string) => {
		setOldSimNumber(oldSimNumber);
		setDialogOpen('replaceCard');
	};

	const showRemoveCardDialog = (simNumber: string) => {
		setSimNumber(simNumber);
		setDialogOpen('removeCard');
	};

	const showAddCardDialog = () => {
		setDialogOpen('addCard');
	};

	const hideAdditionalDialog = () => {
		setDialogOpen(undefined);
		setSimNumber('');
	};

	const availableSimCardsLength = useMemo(() => simsDetails?.filter((details) => !!details?.serialNo).length || 0, [
		simsDetails,
	]);

	const onChangeCardData = (value: string) => {
		setSimNumber(value);
		setErrorMessage('');
	};

	const addCard = () => {
		// serialNumber for esim is created in backend by requesting external api
		if (cardType === CardType.ESIM || simNumberValid) {
			setLoading(true);
			setIsSIMError(false);
			SimService.addSimCard(simType, {
				msisdn: phoneNumber,
				// serialNumber for esim is created in backend by requesting external api
				serialNo: cardType === CardType.SIM ? simNumber : null,
				otp: otp,
			})
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(
					() => {
						dispatch(
							setToast({
								error: false,
								label: translate(`numbers.settings.${simType.replace('-', '.')}.add.success`),
								actionType: translate('success'),
							})
						);
						reload();
					},
					(error) => {
						if (error.response.errorCode === ERROR_VALIDATING_TOKEN) {
							setIsInCorrectOtp(true);
						} else {
							setIsSIMError(true);
							setErrorMessage(
								translate(
									`numbers.settings.sim.details.add.new.error${
										error?.response?.errorDesc?.includes(ADD_SIM_CARD_ERROR) ? '.exists' : ''
									}`
								)
							);
						}
					}
				);
		}
	};

	const replaceCard = () => {
		// serialNumber for esim is created in backend by requesting external api
		if (CardType.ESIM === cardType || simNumberValid) {
			setLoading(true);
			SimService.replaceSimCard(simType, {
				msisdn: phoneNumber,
				serialNo: simNumber,
				oldSerialNo: oldSimNumber,
			})
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(
					() => {
						dispatch(
							setToast({
								error: false,
								label: translate(`numbers.settings.${simType.replace('-', '.')}.add.success`),
								actionType: translate('success'),
							})
						);
						reload();
					},
					(error) => {
						setErrorMessage(
							translate(
								`numbers.settings.sim.details.add.new.error${
									error?.response?.errorDesc?.includes(ADD_SIM_CARD_ERROR) ? '.exists' : ''
								}`
							)
						);
					}
				);
		}
	};

	const addReplaceSimCard = () => {
		if (dialogOpen === 'addCard') {
			addCard();
		} else if (dialogOpen === 'replaceCard') {
			replaceCard();
		}
	};

	const deleteCard = () => {
		setLoading(true);
		SimService.deleteSimCard(simType, {
			msisdn: phoneNumber,
			serialNo: simNumber,
		})
			.pipe(
				finalize(() => {
					setLoading(false);
				})
			)
			.subscribe(
				() => {
					dispatch(
						setToast({
							error: false,
							label: translate(`numbers.settings.${simType.replace('-', '.')}.delete.success`),
							actionType: translate('success'),
						})
					);
					reload();
				},
				() => {
					dispatch(
						setToast({
							error: true,
							label: translate('numbers.settings.sim.details.delete.error'),
							actionType: translate('error'),
						})
					);
				}
			);
	};

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			header={headerName}
			loading={loading}
			width={800}
			dialogBody={
				<div
					className={classNames(classes.simDialogWrapper, {
						[classes.simDialogWrapperWithOneCard]: simsDetails?.length === 1,
					})}
				>
					<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} withData={true} />
					<div className={classes.pinPukCardsContainer}>
						{(simsDetails || []).map((simData, index) => (
							<SimData
								phoneNumber={phoneNumber}
								simType={simType}
								key={`sim-details-${index}`}
								simsDetail={simData}
								showReplaceSimCardDialog={() => showReplaceSimCardDialog(simData?.serialNo || '')}
								showReplaceEsimCardDialog={() => showReplaceEsimCardDialog(simData?.serialNo || '')}
								showRemoveCardDialog={() => showRemoveCardDialog(simData?.serialNo || '')}
							/>
						))}
					</div>
					<div>
						<ConditionalRender
							show={availableSimCardsLength < maxSimCards}
							onTrue={() => (
								<div className={classes.addSimCardWrapper}>
									<IconButton
										onClick={() => {
											setCardType(CardType.SIM);
											showAddCardDialog();
										}}
									>
										<Add />
									</IconButton>
									<Button
										color="tertiary-light"
										onClick={() => {
											setCardType(CardType.SIM);
											showAddCardDialog();
										}}
									>
										{translate('numbers.settings.sim.details.add.new.sim')}
									</Button>
								</div>
							)}
						/>
						<ConditionalRender
							show={availableSimCardsLength < maxSimCards && simType != SimType.DATA_SIM}
							onTrue={() => (
								<div className={classes.addSimCardWrapper}>
									<IconButton
										onClick={() => {
											setCardType(CardType.ESIM);
											showAddCardDialog();
										}}
									>
										<Add />
									</IconButton>
									<Button color="tertiary-light" onClick={showAddCardDialog}>
										{translate('numbers.settings.esim.details.add.new.esim')}
									</Button>
								</div>
							)}
						/>
						{/* show charge disclaimer if is present */}
						<ConditionalRender
							show={!!simMgmtLabels.chargeBanner && simType !== SimType.SAWA_SIM}
							onTrue={() => <ChargeBanner value={simMgmtLabels.chargeBanner} />}
						/>
					</div>
					<RemoveConfirmationDialog
						simType={simType}
						isDialogOpen={dialogOpen === 'removeCard'}
						hideDialog={hideAdditionalDialog}
						deleteCard={deleteCard}
						loading={loading}
					/>
					<AuthConsumer>
						{({ user }) => (
							<ReplaceSimDialog
								isOpen={dialogOpen === 'replaceCard' || dialogOpen === 'addCard'}
								hideDialog={hideAdditionalDialog}
								simNumber={simNumber}
								onChange={onChangeCardData}
								addReplaceSimCard={addReplaceSimCard}
								simNumberValid={simNumberValid}
								setSimNumberValid={setSimNumberValid}
								buttonText={dialogOpen === 'replaceCard' ? translate('replace') : translate('add')}
								errorMessage={errorMessage}
								setErrorMessage={setErrorMessage}
								simType={simType}
								loading={loading}
								cardType={cardType}
								msisdn={user.contactNumber1}
								otp={otp}
								setOtp={setOtp}
								isIncorrectOtp={isIncorrectOtp}
								setIsInCorrectOtp={setIsInCorrectOtp}
								isSIMError={isSIMError}
							/>
						)}
					</AuthConsumer>
				</div>
			}
		/>
	);
};

export default SimDialog;
